

























import { Component, Ref, Vue, Watch } from "vue-property-decorator";
import BaseDialogComponent from "@/components/common/BaseDialog.vue";
import ConfirmationDialogComponent from "@/components/common/ConfirmationDialog.vue";
import { setClientMiraAllClients } from "@/data/repository/ClientAdministrationRepository";
import { SnackbarModule } from "@/store/modules/SnackbarModule";

/**
 * Switches "Mira: Alle Mandanten" on an IRES account, or off (#2610111): its Mira then shows every
 * published Mandant added up, from the next night run. IRES Cloud repeats every check, refuses it on any
 * account that is not IRES's own, records who changed it, and removes the figures at once when it goes off
 * (`setClientMiraAllClientsForAdministration`).
 *
 * `show()` resolves with true once saved.
 */
@Component({ components: { "base-dialog": BaseDialogComponent, "confirmation-dialog": ConfirmationDialogComponent } })
export default class MiraAllClientsDialogComponent extends Vue {
  @Ref("baseDialog")
  baseDialog!: BaseDialogComponent;

  @Ref("confirm")
  confirmDialog!: ConfirmationDialogComponent;

  busy = false;
  clientId = "";
  enabled = false;
  initialEnabled = false;

  private resolve: ((saved: boolean) => void) | null = null;

  get hasChanged(): boolean {
    return this.enabled !== this.initialEnabled;
  }

  /**
   * @param clientId - the account.
   * @param currentEnabled - whether the switch is on now.
   */
  show(clientId: string, currentEnabled: boolean): Promise<boolean> {
    this.clientId = clientId;
    this.initialEnabled = currentEnabled;
    this.enabled = currentEnabled;
    this.baseDialog.show({
      title: this.$t("admin.clients.miraAllClients.editTitle").toString(),
      positiveButtonText: this.$t("common.save").toString(),
      negativeButtonText: this.$t("common.close").toString(),
      maxWidth: 520,
      persistent: true,
      closeOnSave: false,
    });
    return new Promise((resolve) => (this.resolve = resolve));
  }

  /** SCHLIEßEN while nothing changed, ABBRECHEN after (edit-mode-dialogs.md). */
  @Watch("hasChanged")
  onHasChanged(hasChanged: boolean) {
    this.baseDialog.negativeButtonText = this.$t(hasChanged ? "common.abort" : "common.close").toString();
  }

  /** SCHLIEßEN has already closed the dialog; ABBRECHEN asks first. */
  async onNegative() {
    if (!this.hasChanged) {
      this.finish(false);
      return;
    }
    const discard = await this.confirmDialog.show({
      title: this.$t("common.confirmationDialog.unsavedChangesTitle").toString(),
      message: this.$t("common.confirmationDialog.unsavedChangesMessage").toString(),
      acceptMessage: this.$t("common.abort").toString(),
      rejectMessage: this.$t("common.back").toString(),
    });
    if (discard) {
      this.close(false);
    }
  }

  async onSave() {
    if (!this.hasChanged) {
      return;
    }
    this.busy = true;
    try {
      await setClientMiraAllClients(this.clientId, this.enabled);
      SnackbarModule.showSuccess({ key: this.enabled ? "admin.clients.miraAllClients.savedOn" : "admin.clients.miraAllClients.savedOff" });
      this.close(true);
    } catch (error) {
      // Cloud refuses a customer's account; anything else is a stale screen or a lost connection.
      const reason = (error as { details?: { reason?: string } } | null)?.details?.reason;
      SnackbarModule.showError({
        key: reason === "NOT_IRES_ACCOUNT" ? "admin.clients.miraAllClients.notIresAccount" : "admin.clients.miraAllClients.saveFailed",
      });
    } finally {
      this.busy = false;
    }
  }

  close(saved: boolean) {
    this.baseDialog.isShowing = false;
    this.finish(saved);
  }

  private finish(saved: boolean) {
    this.resolve?.(saved);
    this.resolve = null;
  }
}
